import { List, Box, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMenu from '../../useMenu';
import { theme } from '@theguarantors/tg-package-material-ui';

import MenuContent from '@/appWrapper/MenuContent/V2';
import MenuVisibilityControl from './MenuVisibilityControl';
import { Transition } from 'react-transition-group';

const SideMenu = () => {
  const { menuOpen, openMenu, closeMenu } = useMenu();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const getSideMenuMinHeight = (elementId: string): string => {
    const element = document.getElementById(elementId);
    const numberOfFirstColumnIcons = element ? element.children.length : 0;
    return `${(numberOfFirstColumnIcons + 1) * 50}px`;
  };

  const sideMenuMinHeight = getSideMenuMinHeight('firstColumnIcons');
  const baseBackgroundColor = theme.palette.coolGrey[50] ?? '#F6F6F7';
  const borderColor = theme.palette.neutralGrey[200];
  const baseTextColor = theme.palette.neutralGrey[500];

  // Shared menu content
  const menuContent = (
    <List
      sx={{
        backgroundColor: baseBackgroundColor,
        '@media (max-height: 580px)': {
          minHeight: sideMenuMinHeight,
        },
        color: 'white',
        zIndex: 10,
        '& .MuiSvgIcon-root': { fill: 'white' },
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <MenuVisibilityControl
        menuOpen={menuOpen}
        openMenu={openMenu}
        closeMenu={closeMenu}
        baseTextColor={baseTextColor}
        baseBackgroundColor={baseBackgroundColor}
        isMobile={isMobile}
      />

      <MenuContent menuOpen={menuOpen} closeMenu={closeMenu} />
    </List>
  );

  // Mobile: Drawer that slides in/out
  if (isMobile) {
    return (
      <Drawer
        transitionDuration={300}
        variant="temporary"
        open={menuOpen}
        anchor="left"
        onClose={closeMenu}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiSvgIcon-root': { fill: 'white' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '280px',
            backgroundColor: baseBackgroundColor,
            borderRight: `1px solid ${borderColor}`,
          },
        }}
      >
        {menuContent}
      </Drawer>
    );
  }

  // Desktop: Always-visible sidebar that expands/collapses
  return (
    <Transition in={menuOpen} timeout={10}>
      {(state) => (
        <Box
          id="sideMenu"
          sx={{
            height: '100vh',
            position: 'sticky',
            top: 0,
            display: 'flex',
            zIndex: 1000,
            transition: 'max-width 250ms linear',
            maxWidth: state === 'entered' ? '280px' : '72px',
            borderRight: `1px solid ${borderColor}`,
          }}
        >
          {menuContent}
        </Box>
      )}
    </Transition>
  );
};

export default SideMenu;
